import React from 'react'
import { IoCalendarOutline, IoEllipseSharp } from 'react-icons/io5'
import { Widget } from '../Widget'
import { useFixtures } from '../../../swr-config'
import { fmtKickoff } from '../../../utils'
import type { PageId } from '../../BottomNav'
import type { FixtureLite } from '../../../types'

interface TodaysFixturesWidgetProps {
  isEditing?: boolean
  onRemove?: () => void
  onNavigate?: (page: PageId) => void
  maxItems?: number
}

function getStatusLabel(fixture: FixtureLite): string {
  if (fixture.status === 'LIVE') {
    return fixture.elapsed ? `${fixture.elapsed}'` : 'LIVE'
  }
  if (fixture.status === 'FT') return 'FT'
  if (fixture.status === 'HT') return 'HT'
  if (fixture.status === 'PST') return 'PST'
  return fmtKickoff(fixture.kickoff_utc)
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'LIVE':
      return 'fixture-status--live'
    case 'FT':
      return 'fixture-status--finished'
    case 'HT':
      return 'fixture-status--halftime'
    case 'PST':
      return 'fixture-status--postponed'
    default:
      return 'fixture-status--upcoming'
  }
}

export function TodaysFixturesWidget({
  isEditing,
  onRemove,
  onNavigate,
  maxItems = 8,
}: TodaysFixturesWidgetProps) {
  // Use Live Today mode (api_league_id: -1) to get all fixtures
  const { data: fixtures, error } = useFixtures(undefined, 1)

  // Infer loading state from data/error
  const isLoading = !fixtures && !error

  const displayFixtures = (fixtures as FixtureLite[] | undefined)?.slice(0, maxItems) || []

  const handleFixtureClick = (fixture: FixtureLite) => {
    // Navigate to match page
    window.history.pushState(null, '', `#match/${fixture.id}`)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  return (
    <Widget
      id="fixtures"
      title="Today's Fixtures"
      icon={<IoCalendarOutline size={18} />}
      linkTo="fixtures"
      isEditing={isEditing}
      onRemove={onRemove}
      onNavigate={onNavigate}
      isLoading={isLoading}
      error={error ? 'Failed to load fixtures' : null}
      skeletonVariant="fixtures"
    >
      <div className="widget-fixtures">
        {displayFixtures.length === 0 ? (
          <div className="widget-empty">No fixtures today</div>
        ) : (
          <ul className="widget-fixtures-list">
            {displayFixtures.map((fixture: FixtureLite) => (
              <li
                key={fixture.id}
                className="widget-fixture-item"
                onClick={() => handleFixtureClick(fixture)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleFixtureClick(fixture)
                  }
                }}
              >
                <div className="widget-fixture-teams">
                  <span className="widget-fixture-team">{fixture.home_team?.name || 'TBD'}</span>
                  <span className="widget-fixture-vs">vs</span>
                  <span className="widget-fixture-team">{fixture.away_team?.name || 'TBD'}</span>
                </div>
                <div className="widget-fixture-info">
                  {fixture.status === 'LIVE' || fixture.status === 'FT' || fixture.status === 'HT' ? (
                    <span className="widget-fixture-score">
                      {fixture.home_score ?? 0} - {fixture.away_score ?? 0}
                    </span>
                  ) : null}
                  <span className={`widget-fixture-status ${getStatusClass(fixture.status)}`}>
                    {fixture.status === 'LIVE' && (
                      <IoEllipseSharp size={6} className="live-indicator" />
                    )}
                    {getStatusLabel(fixture)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        {fixtures && fixtures.length > maxItems && (
          <button
            className="widget-view-all"
            onClick={() => onNavigate?.('fixtures')}
          >
            View all {fixtures.length} fixtures
          </button>
        )}
      </div>
    </Widget>
  )
}
